import { useCallback, useRef, useState } from "react";
import type { View } from "react-native";
import { useClickOutside } from "../../hooks/use-click-outside";

export interface UsePopoverReturn {
  readonly isOpen: boolean;
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
  /** Attach to the container View so web click-away can detect outside presses. */
  readonly containerRef: React.RefObject<View | null>;
}

/**
 * Minimal open/close state for an anchored popover, with web click-away
 * dismissal. Native dismissal is handled by a backdrop in {@link Popover}.
 */
export function usePopover(): UsePopoverReturn {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<View>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((o) => !o), []);

  useClickOutside(containerRef, close, isOpen);

  return { isOpen, open, close, toggle, containerRef };
}
